import {AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, Optional} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {TemplateVariable, TEMPLATE_VARIABLE_GROUPS} from '../../froala/template-variables';

export interface TemplateVariablePickerComponent extends Translatable {}

/** Keeps answer rows associated with their question even when titles are identical. */
interface PickerQuestion {
  id: string;
  title: string;
  variables: TemplateVariable[];
}

/** Separates grouped question answers from compact variable rows. */
interface PickerGroup {
  id: string;
  label: string;
  icon: string;
  variables: TemplateVariable[];
  questions: PickerQuestion[];
}

/** Offers searchable, keyboard-accessible values without exposing export-only mappings. */
@Component({
  selector: 'app-qa-template-variable-picker',
  templateUrl: './template-variable-picker.component.html',
  styleUrls: ['./template-variable-picker.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class TemplateVariablePickerComponent implements OnInit, AfterViewInit {
  @Input() variables: TemplateVariable[] = [];
  @Input() title = KEYS.EDITOR.INSERT_TEXT_VARIABLE;
  @Input() hint = KEYS.EDITOR.TEXT_VARIABLE_HINT;
  @Input() footer = KEYS.EDITOR.TEXT_VARIABLE_VALUE_HINT;
  @Input() showPaths = true;
  @Input() allLabel = KEYS.EDITOR.TEXT_VARIABLE_ALL;
  /** Path of the variable that is currently used, it is marked and scrolled into view */
  @Input() selected = '';
  public groups = TEMPLATE_VARIABLE_GROUPS;
  public counts: {[group: string]: number} = {};
  public query = '';
  public activeGroup = '';
  public visibleGroups: PickerGroup[] = [];
  public collapsedQuestions = new Set<string>();
  public searching = false;
  public resultCount = 0;

  /** Injects the dialog controller and the current administration language. */
  public constructor(
    public activeModal: NgbActiveModal,
    @Optional() private translationService: TranslationService,
    private element: ElementRef<HTMLElement>
  ) {}

  /** Shows the currently used variable in the middle of the list */
  public ngAfterViewInit(): void {
    if (!this.selected) { return; }
    setTimeout(() => {
      const option = this.element.nativeElement.querySelector('.qa-variable-selected') as HTMLElement;
      option && option.scrollIntoView({block: 'center'});
    });
  }

  /** Prepares stable counts and the initial grouped result list. */
  public ngOnInit(): void {
    this.groups.forEach(group => this.counts[group.id] = this.variables.filter(variable => variable.group === group.id).length);
    this.filter();
  }

  /** Filters by friendly name or technical path and supports the patient alias. */
  public filter(): void {
    const terms = this.query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    this.searching = terms.length > 0;
    this.resultCount = 0;
    this.visibleGroups = this.groups.filter(group => !this.activeGroup || group.id === this.activeGroup).map(group => {
      const matches = this.variables.filter(variable => {
        const context = variable.questionContext;
        const hierarchy = context ? `${context.questionTitle} ${context.ancestors.join(' ')} ${context.label}` : '';
        const haystack = `${variable.label} ${hierarchy} ${variable.path} ${variable.path.replace(/^client\./, 'patient.')} ${this._(group.label)}`
          .toLocaleLowerCase();
        return variable.group === group.id && terms.every(term => haystack.indexOf(term) !== -1);
      });
      const questions = new Map<string, PickerQuestion>();
      const variables = matches.filter(variable => {
        const context = variable.questionContext;
        if (!context) { return true; }
        if (!questions.has(context.questionId)) {
          questions.set(context.questionId, {id: context.questionId, title: context.questionTitle, variables: []});
        }
        questions.get(context.questionId).variables.push(variable);
        return false;
      });
      this.resultCount += matches.length;
      return {...group, variables, questions: Array.from(questions.values())};
    }).filter(group => group.variables.length > 0 || group.questions.length > 0);
  }

  /** Collapses question groups while keeping matching answers expanded during a search. */
  public toggleQuestion(id: string): void {
    if (this.searching) { return; }
    if (this.collapsedQuestions.has(id)) {
      this.collapsedQuestions.delete(id);
      return;
    }
    this.collapsedQuestions.add(id);
  }

  /** Switches categories while preserving the current search. */
  public selectGroup(group: string): void {
    this.activeGroup = group;
    this.filter();
  }

  /** Prevents the questionnaire save shortcut from firing behind the modal. */
  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
